import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/axios';
import { API_ROUTES } from '@/constants';
import type { AdminNumber, NotificationLogResponse } from '@/types/notification';

const KEYS = {
  all: ['notifications'] as const,
  numbers: () => [...KEYS.all, 'numbers'] as const,
  logs: (page?: number) => [...KEYS.all, 'logs', page] as const,
};

const PAGE_SIZE = 15;

// ── Admin Numbers ──

export function useAdminNumbers() {
  return useQuery({
    queryKey: KEYS.numbers(),
    queryFn: async () => {
      const { data } = await api.get<AdminNumber[]>(API_ROUTES.notificationNumbers);
      return data;
    },
  });
}

export function useAddAdminNumber() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { phone_number: string; label: string }) => {
      const { data } = await api.post<AdminNumber>(API_ROUTES.notificationNumbers, payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.numbers() }),
  });
}

export function useUpdateAdminNumber() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: { id: string; is_active?: boolean; label?: string }) => {
      const { data } = await api.patch<AdminNumber>(
        `${API_ROUTES.notificationNumbers}${id}/`,
        payload,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.numbers() }),
  });
}

export function useDeleteAdminNumber() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`${API_ROUTES.notificationNumbers}${id}/`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.numbers() }),
  });
}

// ── Notification Logs ──

export function useNotificationLogs(page = 1) {
  return useQuery({
    queryKey: KEYS.logs(page),
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String((page - 1) * PAGE_SIZE),
      });
      const { data } = await api.get<NotificationLogResponse>(
        `${API_ROUTES.notificationLogs}?${params}`,
      );
      return data;
    },
  });
}

// ── Test Notification ──

export function useTestNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{ message: string }>(API_ROUTES.notificationTest);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.logs() }),
  });
}
